/**
 * `useRecordsWaiting(actor, source)` (13-29): the register records waiting on the signed-in person, for Today's
 * "Records waiting on you" section and the briefing's records line.
 *
 *   - One paged list read per register the actor's lens touches (`recordQueue.lensRegisters`), each with only the
 *     columns the lens reads (`recordService.listWaitingRecords`, `$expand=fields($select=…)`). No read per row and
 *     no read per card (ISS-207). For Oversight, when an incident is `Evaluating`, ONE more read gives every
 *     incident's preparer (`listIncidentPreparers`, D-13-L-19); if it fails, those incidents are still shown, as
 *     "the record page checks who prepared it", and the section says so.
 *   - A failed register read is an error, said in one plain line with a retry, never a blank section: a silently
 *     empty read is a swallowed 4xx (13-23). A failed refresh keeps the last good view on screen and says so.
 *   - The view is pinned to the last read time and refetched on window focus, at most once a minute (D-13-09-3).
 *   - In practice mode the source adds the person's own practice records (`qms.practice.records.v1`), so a practice
 *     lodge waits where a live one would. Practice records never mix into a live read.
 *
 * The source is injected (`RecordsWaitingSource`), so design/today-preview renders the section with synthetic rows.
 * Loading is derived (the latest result is not for the latest request), never set inside the effect (ISS-185).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isOversight } from '../lib/actionAuthority';
import { groupWaiting, lensRegisters, type WaitingGroup } from '../lib/recordQueue';
import { recordsLine } from '../lib/briefing';
import { listIncidentPreparers, listWaitingRecords, preparedByUpn } from '../services/recordService';
import type { RecordIO } from '../services/recordService';
import { practiceRecordStore, readPracticeRecord } from '../services/commitAdapter';
import type { PracticeRecordStore } from '../services/commitAdapter';
import { liveChoice } from '../config/fieldset/live';
import type { Actor } from '../types/actionRegister';
import type { RecordRegister, RecordRow } from '../types/records';

/** The focus refetch floor (D-13-09-3). */
export const RECORDS_REFETCH_MIN_MS = 60_000;

/** Where the rows come from: the app's Graph reads, or the preview's synthetic rows. */
export interface RecordsWaitingSource {
  /** Every row of one register, with at least the columns the lens reads. Throws on a failed read. */
  listRegister(register: RecordRegister): Promise<RecordRow[]>;
  /** Each Evaluating incident's preparer, by ref (one read). Optional: without it, approvals show as unknown. */
  listPreparers?(): Promise<Readonly<Record<string, string | undefined>>>;
}

const [EVALUATING] = liveChoice('incidents', 'Status', 'Evaluating');

/**
 * The app's source: the live list reads, plus (practice mode) the person's own practice records, which only a
 * practice ref can name (D-13-23-1), so the two never collide.
 */
export function liveRecordsSource(
  io: RecordIO,
  opts: { practiceUpn?: string | null; practiceStore?: PracticeRecordStore } = {},
): RecordsWaitingSource {
  const upn = (opts.practiceUpn ?? '').trim().toLowerCase();
  const practiceOf = (register: RecordRegister) => {
    if (!upn) return [];
    const store = opts.practiceStore ?? practiceRecordStore();
    return store
      .list()
      .filter((r) => r.register === register && r.upn === upn)
      .map((r) => readPracticeRecord(r.ref, store))
      .filter((r): r is NonNullable<typeof r> => r !== undefined);
  };
  return {
    listRegister: async (register) => [...(await listWaitingRecords(io, register)), ...practiceOf(register).map((r) => r.row)],
    listPreparers: async () => {
      const live = await listIncidentPreparers(io);
      const practice = Object.fromEntries(practiceOf('incidents').map((r) => [r.row.ref, preparedByUpn(r.siblings)]));
      return { ...live, ...practice };
    },
  };
}

export type RecordsWaitingStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseRecordsWaitingResult {
  /** `idle`: no actor or source yet, or a lens that reads nothing. */
  status: RecordsWaitingStatus;
  groups: WaitingGroup[];
  /** The briefing's records line ("3 records waiting on you, 1 decision due"), "" when none. */
  line: string;
  /** Why the read failed (status `error`, nothing to show). */
  error: string | null;
  /** A refresh failed while an earlier view is shown. */
  refreshError: string | null;
  /** The preparer read failed: approvals show as "the record page checks who prepared it". */
  preparerNote: string | null;
  /** When the shown view was read (epoch ms), or null. */
  readAt: number | null;
  reload: () => void;
}

interface Loaded {
  key: string;
  rows: RecordRow[];
  preparers: Readonly<Record<string, string | undefined>> | null;
  preparerError: boolean;
  readAt: number;
  error: string | null;
}

const message = (err: unknown) => (err instanceof Error ? err.message : 'The register records could not be read.');

export function useRecordsWaiting(
  actor: Actor | null,
  source: RecordsWaitingSource | null,
  opts: { now?: () => number } = {},
): UseRecordsWaitingResult {
  const clock = opts.now ?? Date.now;
  const [trigger, setTrigger] = useState(0);
  const lastLoad = useRef(0);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [good, setGood] = useState<Loaded | null>(null);

  const registers = useMemo(() => (actor ? lensRegisters(actor) : []), [actor]);
  const who = actor ? `${actor.upn.trim().toLowerCase()}|${actor.roles.join(',')}` : '';
  const key = `${who}|${registers.join(',')}|${trigger}`;
  const ready = !!actor && !!source && registers.length > 0;

  useEffect(() => {
    if (!ready || !actor || !source) return;
    let cancelled = false;
    const readAt = clock();
    lastLoad.current = readAt;
    const read = async (): Promise<Loaded> => {
      let rows: RecordRow[];
      try {
        rows = (await Promise.all(registers.map((r) => source.listRegister(r)))).flat();
      } catch (err) {
        return { key, rows: [], preparers: null, preparerError: false, readAt, error: message(err) };
      }
      let preparers: Readonly<Record<string, string | undefined>> | null = null;
      let preparerError = false;
      const evaluating = rows.some((r) => r.register === 'incidents' && r.fields.Status === EVALUATING);
      if (evaluating && isOversight(actor) && source.listPreparers) {
        try {
          preparers = await source.listPreparers();
        } catch {
          preparerError = true;
        }
      }
      return { key, rows, preparers, preparerError, readAt, error: null };
    };
    void read().then((result) => {
      if (cancelled) return;
      setLoaded(result);
      if (result.error === null) setGood(result);
    });
    return () => {
      cancelled = true;
    };
    // `clock` is read at load time only; the key carries everything a reload depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ready, source]);

  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    const onFocus = () => {
      if (Date.now() - lastLoad.current >= RECORDS_REFETCH_MIN_MS) reload();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [reload]);

  return useMemo<UseRecordsWaitingResult>(() => {
    const idle: UseRecordsWaitingResult = {
      status: 'idle',
      groups: [],
      line: '',
      error: null,
      refreshError: null,
      preparerNote: null,
      readAt: null,
      reload,
    };
    if (!ready || !actor) return idle;
    const prefix = `${who}|${registers.join(',')}|`;
    const current = loaded && loaded.key === key ? loaded : null;
    // The last good view for this person and lens (a refresh, or a failed refresh, keeps it on screen).
    const shown = good && good.key.startsWith(prefix) ? good : null;
    if (!shown) {
      if (current && current.error !== null) return { ...idle, status: 'error', error: current.error };
      return { ...idle, status: 'loading' };
    }
    const groups = groupWaiting(shown.rows, actor, new Date(shown.readAt), shown.preparers ? { preparedByUpn: shown.preparers } : {});
    return {
      status: 'ready',
      groups,
      line: recordsLine(groups),
      error: null,
      refreshError: current && current.error !== null ? current.error : null,
      preparerNote: shown.preparerError
        ? "Who prepared each incident waiting for approval couldn't be read, so the record page checks it before approving."
        : null,
      readAt: shown.readAt,
      reload,
    };
  }, [ready, actor, who, registers, loaded, good, key, reload]);
}
